import { useMemo } from "react";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { useLocationContext } from "@/contexts/LocationContext";
import { STATE_LABELS } from "@/lib/availability";
import {
  getReviewsForState,
  reviewStats,
  type LocationReview,
} from "@/data/locationReviews";

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${
            i < rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"
          }`}
        />
      ))}
    </div>
  );
}

function ReviewCard({ review }: { review: LocationReview }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45 }}
      className="relative flex h-full flex-col rounded-xl bg-card p-6 shadow-md transition-shadow hover:shadow-lg"
    >
      <Quote className="absolute right-5 top-5 h-6 w-6 text-primary/10" />
      <div className="mb-3 flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/70 text-sm font-bold text-primary-foreground">
          {review.name.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-foreground">{review.name}</p>
          <StarRating rating={review.rating} />
        </div>
      </div>
      <p className="text-sm leading-relaxed text-foreground/80">"{review.text}"</p>
      <div className="mt-4 flex items-center gap-1.5 pt-1 text-xs text-muted-foreground">
        <img src="https://www.google.com/favicon.ico" alt="Google" className="h-3.5 w-3.5" />
        <span>Posted on Google</span>
      </div>
    </motion.div>
  );
}

interface LocationReviewsProps {
  /** Extra top/bottom padding section wrapper. Default true. */
  withSection?: boolean;
  /** Muted background. Default true. */
  muted?: boolean;
  /** Max reviews to display. Default 6. */
  limit?: number;
  /** Optional heading override. */
  title?: string;
}

export function LocationReviews({
  withSection = true,
  muted = true,
  limit = 6,
  title,
}: LocationReviewsProps) {
  const { state, openSelector } = useLocationContext();
  const reviews = useMemo(() => getReviewsForState(state, limit), [state, limit]);
  const shown = reviews.slice(0, limit);
  const stats = reviewStats(reviews);

  const heading =
    title ??
    (state ? `What customers in ${STATE_LABELS[state]} say` : "What our customers say");

  const content = (
    <div className="container">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="mb-10 text-center"
      >
        <h2 className="mb-3 text-3xl font-bold text-foreground md:text-4xl">{heading}</h2>
        <div className="inline-flex items-center gap-2 rounded-full bg-card px-4 py-2 shadow-sm">
          <img src="https://www.google.com/favicon.ico" alt="Google" className="h-5 w-5" />
          <StarRating rating={Math.round(stats.average)} />
          <span className="text-sm font-medium text-foreground">
            {stats.average.toFixed(1)} from real Google reviews
          </span>
        </div>
        {state && (
          <p className="mt-3 text-sm text-muted-foreground">
            Showing reviews from customers near {STATE_LABELS[state]}.{" "}
            <button onClick={openSelector} className="underline hover:text-accent">
              Change location
            </button>
          </p>
        )}
      </motion.div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {shown.map((review, i) => (
          <ReviewCard key={`${review.name}-${i}`} review={review} />
        ))}
      </div>
    </div>
  );

  if (!withSection) return content;

  return (
    <section className={`section-padding overflow-hidden ${muted ? "bg-muted/50" : ""}`}>
      {content}
    </section>
  );
}
